<template>
  <b-modal
    :visible="visible"
    size="lg"
    centered
    scrollable
    ok-only
    ok-title="Close"
    title="Activity detail"
    @change="(val) => $emit('update:visible', val)"
  >
    <div v-if="entry">
      <!-- The one-line summary, already rendered server-side at the time of the activity. -->
      <div class="d-flex align-items-start mb-1">
        <b-avatar
          :variant="entry.severity_variant"
          size="2.5rem"
          class="mr-1 flex-shrink-0"
        >
          <feather-icon :icon="actionIcon" size="18" />
        </b-avatar>
        <div>
          <h5 class="mb-25">{{ entry.message }}</h5>
          <div>
            <b-badge pill :variant="entry.outcome_variant" class="mr-50">{{ entry.outcome }}</b-badge>
            <b-badge pill :variant="entry.severity_variant" class="mr-50">{{ entry.severity }}</b-badge>
            <b-badge pill variant="light-secondary">{{ entry.module_label }}</b-badge>
          </div>
        </div>
      </div>

      <!--
        The diff, first.

        It is the reason this drawer exists: everything else here is context, but "which fields
        changed, from what, to what" is the question. Values the API redacted are shown as such
        rather than hidden, so the record still proves the field changed.
      -->
      <div v-if="entry.changes && entry.changes.length" class="mb-2">
        <h6 class="section-heading">
          Changes
          <span class="text-muted">({{ entry.changes.length }})</span>
        </h6>
        <b-table-simple small bordered responsive class="mb-0 diff-table">
          <b-thead>
            <b-tr>
              <b-th style="width: 28%">Field</b-th>
              <b-th style="width: 36%">Before</b-th>
              <b-th style="width: 36%">After</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(change, index) in entry.changes" :key="index">
              <b-td class="font-weight-bold">
                {{ change.label || change.field }}
                <div v-if="change.label && change.label !== change.field" class="text-muted small">
                  {{ change.field }}
                </div>
              </b-td>
              <b-td :class="change.redacted ? 'text-muted font-italic' : 'diff-before'">
                {{ format(change.from) }}
              </b-td>
              <b-td :class="change.redacted ? 'text-muted font-italic' : 'diff-after'">
                {{ format(change.to) }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <b-row>
        <b-col cols="12" md="6">
          <h6 class="section-heading">Who</h6>
          <dl class="detail-list">
            <dt>Name</dt>
            <dd>
              <!--
                Links through to the account, but shows the snapshot name. The account may be
                renamed or gone; the log says who it was at the time.
              -->
              <b-link
                v-if="entry.actor.user_id"
                :to="{ name: 'admin-user-single', params: { id: entry.actor.user_id } }"
              >
                {{ entry.actor.name || '—' }}
              </b-link>
              <span v-else>{{ entry.actor.name || '—' }}</span>
            </dd>

            <dt>Email</dt>
            <dd>{{ entry.actor.email || '—' }}</dd>

            <dt>Actor type</dt>
            <dd>{{ entry.actor.type }}</dd>

            <dt v-if="entry.actor.impersonated_by_name">Impersonated by</dt>
            <dd v-if="entry.actor.impersonated_by_name" class="text-danger">
              {{ entry.actor.impersonated_by_name }}
            </dd>

            <dt v-if="entry.actor.api_client_name">API client</dt>
            <dd v-if="entry.actor.api_client_name">{{ entry.actor.api_client_name }}</dd>
          </dl>
        </b-col>

        <b-col cols="12" md="6">
          <h6 class="section-heading">When &amp; where</h6>
          <dl class="detail-list">
            <dt>Time</dt>
            <dd>{{ safeDate(entry.timestamp) }}</dd>

            <dt>IP address</dt>
            <dd>{{ get(entry, 'client.ip_address') || '—' }}</dd>

            <dt>Device</dt>
            <dd>{{ device || '—' }}</dd>

            <dt v-if="get(entry, 'client.country')">Location</dt>
            <dd v-if="get(entry, 'client.country')">
              {{ [get(entry, 'client.city'), get(entry, 'client.country')].filter(Boolean).join(', ') }}
            </dd>
          </dl>
        </b-col>
      </b-row>

      <b-row v-if="entry.target && (entry.target.label || entry.target.id)">
        <b-col cols="12">
          <h6 class="section-heading">Record affected</h6>
          <dl class="detail-list">
            <dt>Label</dt>
            <dd>{{ entry.target.label || '—' }}</dd>

            <dt>Collection</dt>
            <dd>{{ entry.target.collection || '—' }}</dd>

            <dt v-if="entry.target.id">ID</dt>
            <dd v-if="entry.target.id">
              <code>{{ entry.target.id }}</code>
              <!--
                The whole history of this one record. The question an audit trail exists for, and
                the one the previous system could not answer at all.
              -->
              <b-button
                size="sm"
                variant="flat-primary"
                class="ml-50 py-0"
                @click="$emit('view-history', entry.target)"
              >
                View full history
              </b-button>
            </dd>
          </dl>
        </b-col>
      </b-row>

      <b-row v-if="entry.error && entry.error.message">
        <b-col cols="12">
          <h6 class="section-heading text-danger">Error</h6>
          <b-alert show variant="danger" class="p-1 mb-1">
            <strong>{{ entry.error.name }}</strong>: {{ entry.error.message }}
          </b-alert>
          <!-- Stacks are only stored for error/critical rows, so this is often absent. -->
          <details v-if="entry.error.stack">
            <summary class="text-muted small cursor-pointer">Stack trace</summary>
            <pre class="small mt-50 mb-0 stack-trace">{{ entry.error.stack }}</pre>
          </details>
        </b-col>
      </b-row>

      <b-row v-if="entry.request">
        <b-col cols="12">
          <h6 class="section-heading">Request</h6>
          <dl class="detail-list">
            <dt>Route</dt>
            <dd>
              <b-badge variant="light-secondary" class="mr-50">{{ get(entry, 'request.method') }}</b-badge>
              <code>{{ get(entry, 'request.route') || get(entry, 'request.path') || '—' }}</code>
            </dd>

            <dt>Status</dt>
            <dd>{{ get(entry, 'request.status_code') || '—' }}</dd>

            <dt>Duration</dt>
            <dd>{{ get(entry, 'request.duration_ms') !== null ? `${get(entry, 'request.duration_ms')} ms` : '—' }}</dd>

            <dt v-if="get(entry, 'request.result_count') !== null">Records</dt>
            <dd v-if="get(entry, 'request.result_count') !== null">{{ get(entry, 'request.result_count') }}</dd>

            <dt v-if="get(entry, 'request.correlation_id')">Correlation ID</dt>
            <dd v-if="get(entry, 'request.correlation_id')">
              <code>{{ get(entry, 'request.correlation_id') }}</code>
              <!--
                Follows a multi-step operation. A CSV import writes a summary plus a row per
                rejection; they only mean anything read together.
              -->
              <b-button
                size="sm"
                variant="flat-primary"
                class="ml-50 py-0"
                @click="$emit('view-trace', get(entry, 'request.correlation_id'))"
              >
                View related entries
              </b-button>
            </dd>
          </dl>
        </b-col>
      </b-row>

      <!--
        The submitted payload, redacted server-side. Shown collapsed because it is the bulkiest
        part of a row and the least often needed.
      -->
      <b-row v-if="get(entry, 'request.payload')">
        <b-col cols="12">
          <details>
            <summary class="section-heading cursor-pointer">Submitted data</summary>
            <pre class="small mt-50 payload-block">{{ JSON.stringify(get(entry, 'request.payload'), null, 2) }}</pre>
          </details>
        </b-col>
      </b-row>

      <b-row v-if="entry.metadata && Object.keys(entry.metadata).length">
        <b-col cols="12">
          <details>
            <summary class="section-heading cursor-pointer">Additional details</summary>
            <pre class="small mt-50 payload-block">{{ JSON.stringify(entry.metadata, null, 2) }}</pre>
          </details>
        </b-col>
      </b-row>
    </div>

    <div v-else class="text-center py-3">
      <b-spinner small /> <span class="ml-50">Loading…</span>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BBadge, BAvatar, BLink, BButton, BAlert, BSpinner,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'

import { get } from 'lodash'
import { formatDate } from '@core/utils/utils'

/** Per-action icons, so a row's nature is recognisable before the text is read. */
const ACTION_ICONS = {
  create: 'PlusCircleIcon',
  update: 'EditIcon',
  delete: 'Trash2Icon',
  bulk_delete: 'Trash2Icon',
  login: 'LogInIcon',
  login_failed: 'AlertTriangleIcon',
  logout: 'LogOutIcon',
  export: 'DownloadIcon',
  import: 'UploadIcon',
  access_denied: 'LockIcon',
  account_locked: 'LockIcon',
  password_reset: 'KeyIcon',
  settings_change: 'SettingsIcon',
  role_assign: 'UserCheckIcon',
  page_view: 'EyeIcon',
};

export default {
  name: 'ActivityLogDetail',
  components: {
    BModal,
    BRow,
    BCol,
    BBadge,
    BAvatar,
    BLink,
    BButton,
    BAlert,
    BSpinner,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** Null while the entry is loading. */
    entry: {
      type: Object,
      default: null,
    },
  },
  computed: {
    actionIcon() {
      return ACTION_ICONS[get(this.entry, 'action')] || 'ActivityIcon';
    },
    device() {
      return [get(this.entry, 'client.device_type'), get(this.entry, 'client.device_name')]
        .filter(Boolean)
        .join(' / ');
    },
  },
  methods: {
    get,
    /** See the note on `safeDate` in the activity page: an unstamped row must not blank the view. */
    safeDate(value, pattern = 'dd MMM yyyy, HH:mm:ss') {
      if (!value) return '—';
      try {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '—';
        return formatDate(parsed, pattern);
      } catch {
        return '—';
      }
    },
    /**
     * Renders a diff value for display.
     *
     * An empty value is shown as an em dash rather than as blank, so "this field was cleared" is
     * visible as a change instead of looking like a rendering gap.
     */
    format(value) {
      if (value === null || value === undefined || value === '') return '—';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.section-heading {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
}

.detail-list {
  display: grid;
  grid-template-columns: 9rem 1fr;
  row-gap: 0.3rem;
  margin-bottom: 0.5rem;

  dt {
    font-weight: 500;
    opacity: 0.7;
    font-size: 0.85rem;
  }

  dd {
    margin-bottom: 0;
    font-size: 0.9rem;
    word-break: break-word;
  }
}

.diff-table {
  font-size: 0.85rem;

  // The before/after colouring carries the same meaning as a diff elsewhere, so the direction
  // of a change is readable without comparing the two cells word by word.
  .diff-before {
    background-color: rgba(234, 84, 85, 0.08);
  }

  .diff-after {
    background-color: rgba(40, 199, 111, 0.08);
  }
}

.payload-block,
.stack-trace {
  max-height: 240px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.03);
  padding: 0.5rem;
  border-radius: 0.3rem;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
